/**
 * Claim Page - "Almost There!" landing page
 * 
 * Shown when users click the magic link from email but haven't installed the app yet.
 * Provides download links and attempts to open the app via deep link.
 */

import { STORE_LINKS } from './config.mjs';

/**
 * Renders the "Almost There!" claim page
 * @param {HTMLElement} parentEl - Parent element to render into
 * @param {string} token - Magic link token
 */
export function renderClaimPage(parentEl, token) {
    // Clear the container
    parentEl.innerHTML = '';

    // Try to open the app via deep link
    attemptDeepLink(token);

    // Create the main container
    const container = document.createElement('div');
    container.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 80dvh;
        padding: 40px 20px;
        text-align: center;
        animation: fadeIn 0.5s ease-out;
    `;

    // Icon/Emoji
    const icon = document.createElement('div');
    icon.textContent = '📱';
    icon.style.cssText = `
        font-size: 80px;
        margin-bottom: 24px;
        animation: bounce 1s ease-in-out infinite;
    `;

    // Headline
    const headline = document.createElement('h1');
    headline.textContent = 'Almost There!';
    headline.style.cssText = `
        font-size: 36px;
        font-weight: 700;
        color: #1D1B20;
        margin: 0 0 16px 0;
    `;

    // Subheadline
    const subheadline = document.createElement('p');
    subheadline.textContent = 'Install SplitPal Tabs to restore your tabs';
    subheadline.style.cssText = `
        font-size: 18px;
        color: #49454F;
        margin: 0 0 40px 0;
        max-width: 500px;
    `;

    // Store badges container
    const badgesContainer = document.createElement('div');
    badgesContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        margin-bottom: 40px;
    `;

    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);

    const storeLinks = STORE_LINKS;

    // Create store buttons
    if (isIOS || !isAndroid) {
        const appStoreButton = createStoreButton(
            'Download on the App Store',
            storeLinks.ios,
            'https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg'
        );
        badgesContainer.appendChild(appStoreButton);
    }

    if (isAndroid || !isIOS) {
        const playStoreButton = createStoreButton(
            'Get it on Google Play',
            storeLinks.android,
            'https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg'
        );
        badgesContainer.appendChild(playStoreButton);
    }

    // Instructions
    const instructions = document.createElement('div');
    instructions.style.cssText = `
        background: #F5F5F5;
        border-radius: 12px;
        padding: 24px;
        max-width: 500px;
        margin-bottom: 24px;
    `;

    const instructionsTitle = document.createElement('h3');
    instructionsTitle.textContent = 'How it works:';
    instructionsTitle.style.cssText = `
        font-size: 16px;
        font-weight: 600;
        color: #1D1B20;
        margin: 0 0 16px 0;
    `;

    const stepsList = document.createElement('ol');
    stepsList.style.cssText = `
        text-align: left;
        margin: 0;
        padding-left: 20px;
        color: #49454F;
        line-height: 1.8;
    `;

    const steps = [
        'Download SplitPal Tabs',
        'Open the app once',
        'Open this link again'
    ];

    steps.forEach(step => {
        const li = document.createElement('li');
        li.textContent = step;
        li.style.marginBottom = '8px';
        stepsList.appendChild(li);
    });

    instructions.appendChild(instructionsTitle);
    instructions.appendChild(stepsList);

    // Token info (for debugging, hidden in production)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        const tokenInfo = document.createElement('div');
        tokenInfo.style.cssText = `
            font-size: 12px;
            color: #999;
            margin-top: 20px;
            font-family: monospace;
        `;
        tokenInfo.textContent = `Token: ${token}`;
        container.appendChild(tokenInfo);
    }

    // Assemble the page
    container.appendChild(icon);
    container.appendChild(headline);
    container.appendChild(subheadline);
    container.appendChild(badgesContainer);
    container.appendChild(instructions);

    parentEl.appendChild(container);

    // Add animations
    addAnimations();
}

/**
 * Creates a store button
 * @param {string} alt - Alt text
 * @param {string} href - Link URL
 * @param {string} imgSrc - Image source
 * @returns {HTMLElement} Store button element
 */
function createStoreButton(alt, href, imgSrc) {
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.style.cssText = `
        display: inline-block;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        border-radius: 8px;
        overflow: hidden;
    `;

    const img = document.createElement('img');
    img.src = imgSrc;
    img.alt = alt;
    img.style.cssText = `
        height: 60px;
        display: block;
    `;

    link.appendChild(img);

    // Hover effects
    link.addEventListener('mouseenter', () => {
        link.style.transform = 'translateY(-2px)';
        link.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
    });

    link.addEventListener('mouseleave', () => {
        link.style.transform = 'translateY(0)';
        link.style.boxShadow = 'none';
    });

    return link;
}

/**
 * Attempts to open the app via deep link
 * @param {string} token - Magic link token
 */
function attemptDeepLink(token) {
    // Only attempt on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (!isMobile) {
        return;
    }

    // Try to open the app
    const deepLink = `https://tabs.splitpal.io/claim/${token}`;

    // Create a hidden iframe to trigger the deep link
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = deepLink;
    document.body.appendChild(iframe);

    // Remove the iframe after a short delay
    setTimeout(() => {
        document.body.removeChild(iframe);
    }, 1000);

    // Alternative: Use window.location for some platforms
    // This is commented out as iframe method is more reliable
    // window.location.href = deepLink;
}

/**
 * Adds CSS animations to the page
 */
function addAnimations() {
    if (document.getElementById('claim-page-animations')) {
        return; // Already added
    }

    const style = document.createElement('style');
    style.id = 'claim-page-animations';
    style.textContent = `
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        @keyframes bounce {
            0%, 100% {
                transform: translateY(0);
            }
            50% {
                transform: translateY(-10px);
            }
        }
    `;
    document.head.appendChild(style);
}
